import React, { useState, useEffect } from 'react'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import Alert from 'components_new/atoms/Alert'
import Spin from 'components_new/atoms/Spin'
import { setupMFA, verifyMFA } from 'redux/api/MFA'
import { useTranslation } from 'language/translator'

interface MFASetupModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const MFASetupModal: React.FC<MFASetupModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secretCode, setSecretCode] = useState<string | null>(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'setup' | 'verify'>('setup')

  useEffect(() => {
    if (open && step === 'setup') {
      handleSetup()
    }
  }, [open])

  const handleSetup = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await setupMFA()

      setQrCode(response.data.data.qr_code)
      setSecretCode(response.data.data.secret_code)
      setStep('verify')
    } catch (err) {
      setError(t('users', 'mfa_qr_generation_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('users', 'mfa_code_length_error'))

      return
    }

    setLoading(true)
    setError(null)

    try {
      await verifyMFA(verificationCode)
      onSuccess()
      handleClose()
    } catch (err) {
      setError(t('users', 'mfa_invalid_code_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setQrCode(null)
    setSecretCode(null)
    setVerificationCode('')
    setError(null)
    setStep('setup')
    onClose()
  }

  return (
    <SimpleDialog
      open={open}
      onClose={handleClose}
      title={t('users', 'mfa_setup_title')}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 3 }}>
        {loading && step === 'setup' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Spin />
          </Box>
        ) : (
          <>
            {step === 'verify' && qrCode && (
              <>
                <Text variant="body1" sx={{ mb: 2 }}>
                  {t('users', 'mfa_setup_step1')}
                </Text>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    my: 3
                  }}
                >
                  <img
                    src={qrCode}
                    alt="MFA QR Code"
                    style={{ maxWidth: '250px', height: 'auto' }}
                  />
                </Box>

                <Text variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('users', 'mfa_setup_manual')} <strong>{secretCode}</strong>
                </Text>

                <Text variant="body1" sx={{ mb: 2 }}>
                  {t('users', 'mfa_setup_step2')}
                </Text>

                <TextField
                  fullWidth
                  label={t('users', 'mfa_verification_code')}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                    inputMode: 'numeric'
                  }}
                  error={!!error}
                  helperText={error}
                  autoFocus
                  disabled={loading}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 3
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {t('users', 'mfa_cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleVerify}
                    loading={loading}
                    disabled={verificationCode.length !== 6}
                  >
                    {t('users', 'mfa_verify_and_enable')}
                  </Button>
                </Box>
              </>
            )}

            {error && step !== 'verify' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </Box>
    </SimpleDialog>
  )
}

export default MFASetupModal
